import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { sendArticleToTicket } from '../api/send-article-to-ticket.js';

export const ArticleGenerationPage: ReturnType<ReturnType<typeof stateful>> = stateful(() => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ticketID, setTicketID] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: Implement server request
    console.log('Article Generation Form Submitted:', {
      subject,
      body,
      ticketID,
    });

    // Simulate async operation
    await sendArticleToTicket(ticketID, subject, body)();
    await new Promise(resolve => setTimeout(resolve, 500));

    handleReset();
    setIsSubmitting(false);
  };

  const handleReset = () => {
    setSubject('');
    setBody('');
    setTicketID('');
  };

  const isFormValid = subject.trim() !== '' && body.trim() !== '' && ticketID.trim() !== '';

  return html`
    <div class="max-w-4xl mx-auto p-8">
      <div class="mb-6">
        <h2 class="text-3xl font-extrabold text-primary mb-2">Article Generation</h2>
        <p class="text-base text-base-content/70">
          Create a new knowledge base article from ticket information. Fill in the details below to generate an article.
        </p>
      </div>

      <div class="card bg-base-100 shadow-xl rounded-2xl border border-neutral/10">
        <div class="card-body">
          <form @submit=${handleSubmit} class="flex flex-col gap-6">
            <!-- Ticket ID Field -->
            <div class="form-control w-full">
              <label class="label">
                <span class="label-text font-semibold text-base">Ticket ID</span>
                <span class="label-text-alt text-error">Required</span>
              </label>
              <input
                type="text"
                placeholder="Enter ticket correlation ID"
                class="input input-bordered w-full focus:input-primary"
                .value=${ticketID}
                @input=${(e: InputEvent) => setTicketID((e.target as HTMLInputElement).value)}
                required
              />
              <label class="label">
                <span class="label-text-alt text-base-content/60"
                  >The correlation ID of the ticket to generate an article for</span
                >
              </label>
            </div>

            <!-- Subject Field -->
            <div class="form-control w-full">
              <label class="label">
                <span class="label-text font-semibold text-base">Subject</span>
                <span class="label-text-alt text-error">Required</span>
              </label>
              <input
                type="text"
                placeholder="Enter article subject"
                class="input input-bordered w-full focus:input-primary"
                .value=${subject}
                @input=${(e: InputEvent) => setSubject((e.target as HTMLInputElement).value)}
                required
              />
              <label class="label">
                <span class="label-text-alt text-base-content/60">A clear and concise title for the article</span>
              </label>
            </div>

            <!-- Body Field -->
            <div class="form-control w-full">
              <label class="label">
                <span class="label-text font-semibold text-base">Body</span>
                <span class="label-text-alt text-error">Required</span>
              </label>
              <textarea
                placeholder="Enter article content..."
                class="textarea textarea-bordered h-64 w-full focus:textarea-primary resize-y"
                .value=${body}
                @input=${(e: InputEvent) => setBody((e.target as HTMLTextAreaElement).value)}
                required
              ></textarea>
              <label class="label">
                <span class="label-text-alt text-base-content/60">
                  The main content of the article. Include detailed information, steps, or solutions.
                </span>
              </label>
            </div>

            <!-- Character Count -->
            <div class="flex justify-between items-center text-sm text-base-content/60">
              <span>Subject: ${subject.length} characters</span>
              <span>Body: ${body.length} characters</span>
            </div>

            <!-- Action Buttons -->
            <div class="divider"></div>
            <div class="flex gap-3 justify-end">
              <button type="button" class="btn btn-ghost" @click=${handleReset} ?disabled=${isSubmitting}>Reset</button>
              <button type="submit" class="btn btn-primary" ?disabled=${!isFormValid || isSubmitting}>
                ${isSubmitting
                  ? html`<span class="loading loading-spinner loading-sm"></span> Generating...`
                  : 'Generate Article'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Info Card -->
      <div class="alert alert-info shadow-lg mt-6">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
        <div>
          <h3 class="font-bold">Article Generation Tips</h3>
          <div class="text-sm">
            <ul class="list-disc list-inside mt-2 space-y-1">
              <li>Use a descriptive subject that clearly identifies the issue or topic</li>
              <li>Include step-by-step instructions when applicable</li>
              <li>Reference the ticket ID to maintain traceability</li>
              <li>Keep the language clear and accessible for end users</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  `;
});

export default ArticleGenerationPage;
